import { cva, VariantProps } from 'class-variance-authority';
import { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import clsx from 'clsx';
import { Typography, TypographyVariants } from '../Typography';

const TabCVA = cva(
  [
    'border-[--tab-border-default]',
    'p-[--padding-sm]',
    'gap-[--padding-2xs]',
    'flex',
    'flex-row',
    'items-center',
  ],
  {
    variants: {
      borderWidth: {
        default: 'border-2',
        zero: '',
      },
      variant: {
        filled: [
          'disabled:bg-[--tab-bg-disabled]',
          'disabled:border-[--tab-border-disabled]',
          'disabled:text-[--tab-label-disabled]',
          'hover:bg-[--tab-bg-hover]',
        ],
        outlined: [
          'disabled:border-[--tab-border-disabled]',
          'disabled:text-[--tab-label-disabled]',
        ],
      },
      appearance: {
        pill: 'rounded-[--corner-full]',
        square: 'rounded-[--corner-zero]',
        rounded: 'rounded-[--corner-xs]',
      },
      selected: {
        true: '',
        false: 'text-[--tab-label-default]',
      },
      size: {},
    },
    compoundVariants: [
      {
        variant: 'filled',
        selected: true,
        className:
          '!bg-[--tab-bg-selected] text-[--tab-label-selected] border-[--tab-border-selected-filled]',
      },
      {
        variant: 'outlined',
        selected: true,
        className: '',
      },
    ],
  }
);

export interface TabProps extends ComponentProps<'button'> {
  labelVariant?: TypographyVariants;
  labelClassName?: string;

  borderWidth?: Exclude<VariantProps<typeof TabCVA>['borderWidth'], 'null'>;
  appearance?: Exclude<VariantProps<typeof TabCVA>['appearance'], 'null'>;
  variant?: Exclude<NonNullable<VariantProps<typeof TabCVA>['variant']>, 'null'>;

  startAdornment?: ReactNode;
  endAdornment?: ReactNode;

  selected?: boolean;
}

export const Tab = ({
  disabled,
  children,
  selected = false,
  labelClassName,
  variant = 'filled',
  appearance = 'pill',
  labelVariant = 'button020',
  borderWidth = 'default',
  startAdornment,
  endAdornment,
  className,
  ...otherProps
}: PropsWithChildren<TabProps>) => {
  return (
    <button
      className={clsx(TabCVA({ selected, variant, appearance }), className)}
      disabled={disabled}
      {...otherProps}
    >
      {startAdornment}
      <Typography
        as="label"
        variant={labelVariant}
        className={clsx(
          'pointer-events-none flex-1 overflow-hidden text-center text-ellipsis whitespace-nowrap',
          labelClassName
        )}
      >
        {children}
      </Typography>
      {endAdornment}
    </button>
  );
};
